import { create } from 'zustand';
import type { Message, ErrorMessage } from '@/types/message.js';

interface SessionState {
    messages: Message[];
    processing: boolean;
    isStreaming: boolean;
    activeError: ErrorMessage | null;
    loadingHistory: boolean;
}

interface ChatStore {
    // State - organized by session
    sessions: Map<string, SessionState>;

    // Actions
    addMessage: (message: Message) => void;
    updateMessage: (id: string, updates: Partial<Message>) => void;
    setProcessing: (sessionId: string, processing: boolean) => void;
    setStreaming: (sessionId: string, isStreaming: boolean) => void;
    setError: (sessionId: string, error: ErrorMessage | null) => void;
    clearMessages: (sessionId?: string) => void;
    setMessages: (sessionId: string, messages: Message[]) => void;
    setLoadingHistory: (sessionId: string, loading: boolean) => void;

    // Getters
    getSessionState: (sessionId: string) => SessionState;
    getMessages: (sessionId: string) => Message[];
    isProcessing: (sessionId: string) => boolean;
}

const defaultSessionState: SessionState = {
    messages: [],
    processing: false,
    isStreaming: false,
    activeError: null,
    loadingHistory: false,
};

export const useChatStore = create<ChatStore>((set, get) => ({
    // Initial state
    sessions: new Map(),

    // Actions
    addMessage: (message) => {
        const sessionId = message.sessionId || 'default';
        console.log('[ChatStore] addMessage called:', { sessionId, messageId: message.id, messageRole: message.role });
        set((state) => {
            const newSessions = new Map(state.sessions);
            const sessionState = newSessions.get(sessionId) || { ...defaultSessionState };

            // Check if message already exists to prevent duplicates
            const messageExists = sessionState.messages.some(m => m.id === message.id);
            if (messageExists) {
                console.warn('[ChatStore] Message already exists, skipping:', message.id);
                return state; // Return existing state, no update
            }

            newSessions.set(sessionId, {
                ...sessionState,
                messages: [...sessionState.messages, message],
            });
            return { sessions: newSessions };
        });
    },

    updateMessage: (id, updates) => {
        console.log('[ChatStore] updateMessage called:', { id, updates });
        set((state) => {
            const newSessions = new Map(state.sessions);
            let found = false;
            // Find which session contains this message
            for (const [sessionId, sessionState] of newSessions.entries()) {
                const messageIndex = sessionState.messages.findIndex((m) => m.id === id);
                if (messageIndex !== -1) {
                    found = true;
                    const oldMessage = sessionState.messages[messageIndex];

                    // Create completely new array with new message object
                    const updatedMessages = [...sessionState.messages];
                    updatedMessages[messageIndex] = {
                        ...oldMessage,
                        ...updates,
                    } as Message;

                    console.log('[ChatStore] Message updated:', {
                        id,
                        sessionId,
                        messageIndex,
                        oldToolResult: oldMessage.role === 'tool' ? (oldMessage as any).toolResult : 'N/A',
                        newToolResult: 'toolResult' in updates && updates.toolResult !== undefined ? 'HAS_RESULT' : 'NO_RESULT',
                        success: 'toolResultSuccess' in updates ? (updates as any).toolResultSuccess : undefined,
                        oldMessageRef: oldMessage === updatedMessages[messageIndex] ? 'SAME' : 'DIFFERENT',
                        arrayRefChanged: sessionState.messages !== updatedMessages ? 'YES' : 'NO'
                    });

                    // Create new session state with new messages array
                    const newSessionState = {
                        ...sessionState,
                        messages: updatedMessages,
                    };

                    newSessions.set(sessionId, newSessionState);
                    console.log('[ChatStore] Session state updated, new message count:', newSessionState.messages.length);
                    break;
                }
            }
            if (!found) {
                console.warn('[ChatStore] updateMessage: message not found:', id);
            }
            return { sessions: newSessions };
        });
    },

    setProcessing: (sessionId, processing) => {
        set((state) => {
            const newSessions = new Map(state.sessions);
            const sessionState = newSessions.get(sessionId) || { ...defaultSessionState };
            newSessions.set(sessionId, { ...sessionState, processing });
            return { sessions: newSessions };
        });
    },

    setStreaming: (sessionId, isStreaming) => {
        set((state) => {
            const newSessions = new Map(state.sessions);
            const sessionState = newSessions.get(sessionId) || { ...defaultSessionState };
            newSessions.set(sessionId, { ...sessionState, isStreaming });
            return { sessions: newSessions };
        });
    },

    setError: (sessionId, error) => {
        set((state) => {
            const newSessions = new Map(state.sessions);
            const sessionState = newSessions.get(sessionId) || { ...defaultSessionState };
            newSessions.set(sessionId, { ...sessionState, activeError: error });
            return { sessions: newSessions };
        });
    },

    clearMessages: (sessionId) => {
        if (sessionId) {
            set((state) => {
                const newSessions = new Map(state.sessions);
                newSessions.set(sessionId, { ...defaultSessionState });
                return { sessions: newSessions };
            });
        } else {
            set({ sessions: new Map() });
        }
    },

    setMessages: (sessionId, messages) => {
        console.log('[ChatStore] setMessages called:', {
            sessionId,
            messageCount: messages.length,
            messageIds: messages.map(m => m.id).slice(0, 3),
        });
        set((state) => {
            const newSessions = new Map(state.sessions);
            const sessionState = newSessions.get(sessionId) || { ...defaultSessionState };

            // Check if messages are the same to prevent unnecessary updates
            const existingMessages = sessionState.messages;
            if (
                existingMessages.length === messages.length &&
                existingMessages.every((msg, idx) => msg.id === messages[idx]?.id)
            ) {
                console.log('[ChatStore] Messages unchanged, skipping update');
                return state; // Return existing state, no update
            }

            const updatedState = { ...sessionState, messages: [...messages] }; // Create new array reference
            newSessions.set(sessionId, updatedState);
            console.log('[ChatStore] setMessages - map updated, session now has:', updatedState.messages.length, 'messages');
            return { sessions: newSessions };
        });
    },

    setLoadingHistory: (sessionId, loading) => {
        set((state) => {
            const newSessions = new Map(state.sessions);
            const sessionState = newSessions.get(sessionId) || { ...defaultSessionState };
            newSessions.set(sessionId, { ...sessionState, loadingHistory: loading });
            return { sessions: newSessions };
        });
    },

    // Getters
    getSessionState: (sessionId) => {
        const state = get().sessions.get(sessionId);
        if (!state) {
            console.warn('[ChatStore] getSessionState called for non-existent session:', sessionId);
            return { ...defaultSessionState };
        }
        return state;
    },

    getMessages: (sessionId) => {
        const state = get().sessions.get(sessionId);
        if (!state) {
            console.warn('[ChatStore] getMessages called for non-existent session:', sessionId);
            return [];
        }
        return state.messages;
    },

    isProcessing: (sessionId) => {
        return get().sessions.get(sessionId)?.processing || false;
    },
}));
